import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { seoContent } from '@afficheservices/db/schema'
import { eq, and } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { LeadCaptureBlock } from '~/components/lead-capture-block'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { ArticleSchema, HowToSchema } from '~/components/seo-content'
import { getReleaseDates } from '~/lib/content-dates'
import { ContentMeta, SourceCitations } from '~/components/content-trust'
import { AuthorByline, AuthorSchema, getAuthorForCategory } from '~/components/eeat-author'
import { TableOfContents, tocSlug } from '~/components/table-of-contents'
import {
  Snowflake, Sun, Leaf, AlertTriangle, Lightbulb,
  Clock, ShieldAlert, ArrowRight, Home,
} from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

// ── Guide definitions ──
import { GUIDES, GUIDE_SLUGS, getRelatedGuides, allGuideTitles } from '~/lib/guide-data.server'

const BADGE_ICONS = {
  winter: Snowflake,
  spring: Leaf,
  summer: Sun,
  autumn: Leaf,
  urgency: ShieldAlert,
  buyer: Home,
}

export async function loader({ params }: LoaderFunctionArgs) {
  const slug = params.slug ?? ''
  const guide = GUIDES[slug]

  if (!guide) throw new Response('Guide non trouve', { status: 404 })

  // Load AI enrichment if available
  const [aiRow] = await db
    .select({
      expertIntro: seoContent.expertIntro,
      localContext: seoContent.localContext,
      faqItems: seoContent.faqItems,
      expertTips: seoContent.expertTips,
      priceAnalysis: seoContent.priceAnalysis,
    })
    .from(seoContent)
    .where(and(
      eq(seoContent.serviceSlug, 'guide'),
      eq(seoContent.citySlug, slug),
      eq(seoContent.status, 'published'),
    ))
    .limit(1)

  const clean = (t: string) => t.replace(/\\n/g, '\n').replace(/\\t/g, ' ')
  const splitParas = (t: string) => clean(t).split('\n\n').map(p => p.trim()).filter(Boolean)

  const aiContent = aiRow ? {
    expertIntroParas: splitParas(aiRow.expertIntro),
    faqItems: (typeof aiRow.faqItems === 'string' ? JSON.parse(aiRow.faqItems) : aiRow.faqItems) as Array<{ q: string; a: string }>,
    expertTips: (typeof aiRow.expertTips === 'string' ? JSON.parse(aiRow.expertTips) : aiRow.expertTips) as Array<{ title: string; text: string }>,
  } : null

  // Word count drives Article schema wordCount + quality signal. Counts
  // section titles + paragraphs + tips + warnings.
  const wordCount = guide.sections.reduce((sum, s) => {
    let words = s.title.split(/\s+/).length
    words += s.paragraphs.reduce((a, p) => a + p.split(/\s+/).length, 0)
    if (s.tips) words += s.tips.reduce((a, t) => a + t.split(/\s+/).length, 0)
    if (s.warnings) words += s.warnings.reduce((a, w) => a + w.split(/\s+/).length, 0)
    return sum + words
  }, 0)

  // Stable release dates (not new Date()) — Google QRG Sept 2025 flags
  // unjustified "updated today" as a fake-freshness signal. Per-guide
  // datePublished + monthly CURRENT_RELEASE for dateModified gives an
  // honest audit trail. Loader-resolved so SSR + CSR receive identical
  // strings (avoids #418 hydration mismatch too).
  const { datePublished, dateModified } = getReleaseDates(slug)
  // Resolve related + all-guide lists server-side so the GUIDES data map never
  // ships to the client (it is a ~1.3k-line server-only module now).
  return { slug, guide, aiContent, relatedGuides: getRelatedGuides(guide.relatedSlugs), allGuides: allGuideTitles(), wordCount, datePublished, dateModified }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  // SEO truncation: Google SERP cuts titles ~580px (~60 chars) и descriptions
  // ~920px (~160 chars). Long titles like "MaPrimeRénov' 2026 : guide complet
  // (barèmes, parcours, démarches) — Conseils 2026 | AfficheServices" (110
  // chars) heavily truncate visible CTR-critical info. Cap intelligently.
  const fullTitle = data?.guide?.title ?? 'Guide pratique'
  const baseTitle = fullTitle.length > 45 ? fullTitle.slice(0, 42).trim() + '…' : fullTitle
  const title = `${baseTitle} | AfficheServices` // budget ~60 chars total
  const subtitle = data?.guide?.subtitle ?? ''
  const rawDesc = `${fullTitle}. ${subtitle}`
  const description = rawDesc.length > 155 ? rawDesc.slice(0, 152).trim() + '…' : rawDesc
  return [
    { title },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/guide/${data?.slug}` },
  ]
}

export default function GuidePage() {
  const { slug, guide, aiContent, relatedGuides, allGuides, wordCount, datePublished, dateModified } = useLoaderData<typeof loader>()
  const author = getAuthorForCategory(guide.category)
  const BadgeIcon = BADGE_ICONS[guide.badgeIcon]
  const guideUrl = `https://www.afficheservices.fr/guide/${slug}`

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <TableOfContents readingMinutes={guide.readTime} title="Dans ce guide" />
      <FAQSchema items={aiContent?.faqItems?.map(f => ({ question: f.q, answer: f.a })) ?? []} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Guides pratiques', url: 'https://www.afficheservices.fr/guide' },
        { name: guide.title, url: guideUrl },
      ]} />
      <AuthorSchema author={author} articleUrl={guideUrl} />
      {/* Article schema — full E-E-A-T pack (speakable, wordCount, image,
          keywords, articleSection). Drives Google rich results + AI lift. */}
      <ArticleSchema
        title={guide.title}
        description={guide.subtitle}
        url={guideUrl}
        datePublished={datePublished}
        dateModified={dateModified}
        image={`https://www.afficheservices.fr/api/og?title=${encodeURIComponent(guide.title)}`}
        keywords={[guide.category, guide.badge, 'guide pratique', new Date().getFullYear().toString()]}
        articleSection={guide.category}
        wordCount={wordCount}
        authorName={author?.name}
      />
      {/* HowTo — section titles become step titles. Eligible for "How to"
          rich result + AI step-by-step lift. */}
      <HowToSchema
        name={`Comment maîtriser : ${guide.title}`}
        steps={guide.sections.map((s) => ({
          title: s.title,
          detail: s.paragraphs[0] ?? s.title,
        }))}
      />

      <div className="mx-auto max-w-3xl px-4 py-10 lg:px-6">
        <AuthorByline author={author} readTime={guide.readTime} />

        {/* Hero */}
        <div className="mt-4 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 p-8 text-white">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${guide.badgeColor}`}>
              <BadgeIcon className="h-3.5 w-3.5" /> {guide.badge}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-slate-400">
              <Clock className="h-3.5 w-3.5" /> {guide.readTime} min de lecture
            </span>
            <time dateTime={dateModified} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-300">
              Mis à jour le {new Date(dateModified).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </time>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-slate-300">
              {wordCount.toLocaleString('fr-FR')} mots
            </span>
          </div>
          <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{guide.title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-300">{guide.subtitle}</p>
          <div className="mt-5">
            <Link to={`/devis-gratuit?service=${guide.category}`} className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-md hover:bg-slate-100">
              Trouver un artisan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Key Takeaways — AI lift target (Speakable selector matches
            `.key-takeaways`). Each section title becomes a takeaway,
            preceded by the subtitle for context. */}
        <aside className="key-takeaways mt-8 rounded-2xl border border-amber-200 bg-amber-50/50 p-6">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-700">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">✓</span>
            À retenir
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{guide.subtitle}</p>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
            {guide.sections.slice(0, 5).map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                <span>{s.title}</span>
              </li>
            ))}
          </ul>
        </aside>

        {/* AI Expert intro */}
        {aiContent?.expertIntroParas && (
          <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <ContentMeta cityName="France" serviceName={guide.title} />
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">
              {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <SourceCitations serviceSlug={guide.category} />
          </article>
        )}

        {/* Article sections — with mid-article CTA injected after ~50% of sections */}
        <div className="mt-8 space-y-6">
          {guide.sections.map((section, si) => {
            const midIndex = Math.floor(guide.sections.length / 2) - 1
            return (
              <div key={si} className="space-y-6">
                <article className="rounded-2xl border border-border bg-white p-6 shadow-xs">
                  <h2 id={tocSlug(section.title)} className="text-lg font-bold text-slate-900 scroll-mt-24">{section.title}</h2>
                  <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
                    {section.paragraphs.map((p, pi) => <p key={pi}>{p}</p>)}
                  </div>

                  {/* Tips */}
                  {section.tips && section.tips.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {section.tips.map((tip, ti) => (
                        <div key={ti} className="flex gap-3 rounded-xl bg-amber-50 p-3">
                          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                          <p className="text-xs leading-relaxed text-amber-800">{tip}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Warnings */}
                  {section.warnings && section.warnings.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {section.warnings.map((warn, wi) => (
                        <div key={wi} className="flex gap-3 rounded-xl bg-red-50 p-3">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                          <p className="text-xs leading-relaxed text-red-800 font-medium">{warn}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </article>

                {/* Mid-article CTA — inserted after ~half the sections.
                    Renders SSR (static markup, no client-only checks). */}
                {si === midIndex && guide.sections.length > 2 && (
                  <aside className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/60 p-5 shadow-xs">
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Besoin d'un pro ?</p>
                    <h3 className="mt-1.5 text-base font-bold text-slate-900">
                      Trouver un artisan vérifié pour {guide.title.toLowerCase()}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                      Artisans SIRET vérifiés près de chez vous. Réponse sous 48h, sans engagement.
                    </p>
                    <Link
                      to={`/demande-devis/1?service=${guide.category}`}
                      className="mt-3 inline-flex min-h-12 items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-md hover:bg-emerald-700 btn-press"
                    >
                      Recevoir mes 3 devis gratuits <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </aside>
                )}
              </div>
            )
          })}
        </div>

        {/* End-article CTA — placed after the last article section, before
            secondary blocks (AI tips, FAQ, related, lead-capture). */}
        <aside className="mt-8 rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-6 shadow-xs">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Passez à l'action</p>
          <h3 className="mt-1.5 text-lg font-bold text-slate-900">
            Comparer 3 devis gratuits pour {guide.title.toLowerCase()}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Vous avez lu le guide. La prochaine étape : recevoir des prix réels d'artisans qualifiés.
            Réponse en 48h, sans engagement, 100% gratuit.
          </p>
          <Link
            to={`/demande-devis/1?service=${guide.category}`}
            className="mt-4 inline-flex min-h-12 items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:bg-emerald-700 btn-press"
          >
            Recevoir mes 3 devis gratuits <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </aside>

        {/* AI tips */}
        {aiContent?.expertTips && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Conseils supplementaires de nos experts</h2>
            <div className="mt-4 space-y-3">
              {aiContent.expertTips.map((tip, i) => (
                <div key={i} className="rounded-xl bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-900">{tip.title}</h4>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{tip.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQ */}
        {aiContent?.faqItems && aiContent.faqItems.length > 0 && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Questions frequentes</h2>
            <div className="mt-4 space-y-4">
              {aiContent.faqItems.map((faq, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related guides */}
        {relatedGuides.length > 0 && (
          <div className="mt-8 rounded-xl border border-border bg-white p-5 shadow-xs">
            <h3 className="text-sm font-bold text-slate-900">Guides associes</h3>
            <div className="mt-3 space-y-2">
              {relatedGuides.map((g) => (
                <Link key={g.slug} to={`/guide/${g.slug}`} className="flex items-center justify-between rounded-lg border border-border p-3 hover:border-primary hover:bg-slate-50">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{g.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{g.subtitle}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* All guides */}
        <div className="mt-6 rounded-xl border border-border bg-white p-5 shadow-xs">
          <h3 className="text-sm font-bold text-slate-900">Tous les guides pratiques</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {allGuides.filter(g => g.slug !== slug).map((g) => (
              <Link key={g.slug} to={`/guide/${g.slug}`} className="rounded-full border border-border px-3 py-1.5 text-xs text-slate-600 hover:border-primary hover:text-primary">
                {g.title}
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8">
          <LeadCaptureBlock serviceName={guide.category} cityName="votre ville" categorySlug={guide.category} />
        </div>
      </div>

      <Footer />
    </div>
  )
}
